import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { RuntimeConfig } from './config';

export interface Session {
  email: string;
  headers: Record<string, string>;
}

export interface AuthClient {
  mode: 'dev' | 'cognito';
  providers: { id: string; label: string }[];
  getSession(): Promise<Session | null>;
  signIn(opts?: { provider?: string; email?: string; returnTo?: string }): Promise<void>;
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
}

const RETURN_KEY = 'agents.returnTo';

function safeReturnTo(path: string | null | undefined): string {
  return path && path.startsWith('/') && !path.startsWith('//') ? path : '/';
}

class DevAuth implements AuthClient {
  mode = 'dev' as const;
  providers = [];
  private key = 'agents.devUser';

  async getSession(): Promise<Session | null> {
    const email = sessionStorage.getItem(this.key);
    return email ? { email, headers: { 'x-dev-user': email } } : null;
  }

  async signIn(opts?: { email?: string; returnTo?: string }) {
    if (!opts?.email) return;
    sessionStorage.setItem(this.key, opts.email.trim().toLowerCase());
    window.location.assign(safeReturnTo(opts.returnTo));
  }

  async completeSignIn() {
    return '/';
  }

  async signOut() {
    sessionStorage.removeItem(this.key);
    window.location.assign('/login');
  }
}

class CognitoAuth implements AuthClient {
  mode = 'cognito' as const;
  providers: { id: string; label: string }[];
  private manager: UserManager;

  constructor(private readonly cfg: Extract<RuntimeConfig['auth'], { mode: 'cognito' }>) {
    this.providers = cfg.providers;
    this.manager = new UserManager({
      authority: cfg.authority,
      client_id: cfg.clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/login`,
      response_type: 'code',
      scope: 'openid email profile',
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      automaticSilentRenew: true,
    });
  }

  async getSession(): Promise<Session | null> {
    const user = await this.manager.getUser();
    if (!user || user.expired || !user.id_token) return null;
    const email = typeof user.profile.email === 'string' ? user.profile.email : '';
    // The API authorizer validates the ID token (it carries the verified email claim).
    return { email, headers: { authorization: `Bearer ${user.id_token}` } };
  }

  async signIn(opts?: { provider?: string; returnTo?: string }) {
    sessionStorage.setItem(RETURN_KEY, safeReturnTo(opts?.returnTo));
    await this.manager.signinRedirect({
      extraQueryParams: opts?.provider ? { identity_provider: opts.provider } : {},
    });
  }

  async completeSignIn() {
    await this.manager.signinRedirectCallback();
    const to = safeReturnTo(sessionStorage.getItem(RETURN_KEY));
    sessionStorage.removeItem(RETURN_KEY);
    return to;
  }

  async signOut() {
    await this.manager.removeUser();
    // Cognito does not publish end_session_endpoint; its /logout clears the hosted session.
    const url = new URL(`https://${this.cfg.domain}/logout`);
    url.searchParams.set('client_id', this.cfg.clientId);
    url.searchParams.set('logout_uri', `${window.location.origin}/login`);
    window.location.assign(url.toString());
  }
}

export function createAuth(config: RuntimeConfig): AuthClient {
  return config.auth.mode === 'cognito' ? new CognitoAuth(config.auth) : new DevAuth();
}
